import { useEffect, useState } from 'react';
import CropperModal from './DNDCROP/CropperModal';
import Button from '../../../forms/Button';
import { useSelector } from 'react-redux';
import {
  deleteCoverImage,
  setBatchId,
  setCoverImages,
  setIsReusingBatch,
} from '../../../../redux/slice/sketchSlice';
import SortableImageGrid from './DNDCROP/SortableImageGrid';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { saveStep } from '../../../../services/stepService';
import { profileDetails } from '../../../../services/ProfileService';
import { removeBookImage } from '../../../../services/bookService';
import FileUploader from '../../../common/UppyUploader';
import { getActiveStep } from '../../../../services/commonService';
import StepProgressView from '../../../common/StepProgressView';
import { markStepAsVisited } from '../../../../redux/slice/profileSlice';
import { run } from '../../../../services/runpodService';
import { processAllImagesForRun } from '../../../../util/imageCropUploadUtils';

function UploadCoverPhoto() {
  const [croppingImage, setCroppingImage] = useState(null);
  const [deletedFilenames, setDeletedFilenames] = useState([]);
  const [, setProfileDetails] = useState({});
  const [, setIsOpenBookSelectionModal] = useState(false);
  const [nextDisabled, setNextDisabled] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const coverImages = useSelector((state) => state.sketch.coverImages);
  const { activeBookId, uploadingCoverImage } = useSelector((state) => state.stepFlow);
  const { session } = useSelector((state) => state.authProvider);
  const stepNumber = getActiveStep();

  useEffect(() => {
    dispatch(setBatchId(uuidv4()));
    dispatch(setIsReusingBatch(true));
  }, [dispatch]);

  const handleCropSave = ({ blob, url, cropData }) => {
    // Validate required parameters
    if (!blob || !url || !cropData) {
      console.error('Invalid crop data received:', {
        blob: !!blob,
        url: !!url,
        cropData: !!cropData,
      });
      toast.error('Failed to save cropped image. Please try again.');
      return;
    }

    if (!croppingImage) {
      console.error('No cropping image reference found');
      return;
    }

    try {
      // Determine the filename to use with fallback chain
      const getFilename = (img) => {
        if (img.originalFilename) return img.originalFilename;
        if (img.file?.name) return img.file.name;
        if (img.filename) return img.filename;
        return `cropped-image-${Date.now()}.jpg`;
      };

      // Validate blob before creating File
      if (!(blob instanceof Blob)) {
        throw new Error('Invalid blob object received');
      }

      const updated = coverImages.map((img) => {
        const cond = !!img._id
          ? img._id === croppingImage._id
          : img.id === croppingImage.id;

        if (cond) {
          const filename = getFilename(img);

          try {
            const newFile = new File([blob], filename, {
              type: blob.type || 'image/jpeg',
              lastModified: Date.now(),
            });

            return {
              ...img,
              url,
              file: newFile,
              cropData,
              originalUrl: img.originalUrl || img.url,
              originalFilename: img.originalFilename || img.filename || filename,
              lastModified: Date.now(),
              isCropped: true,
              AIinfo: null,
              AIUrl: null
            };
          } catch (fileError) {
            console.error('Error creating File object:', fileError);
            throw new Error(`Failed to create file: ${fileError.message}`);
          }
        }
        return img;
      });

      if (!updated || updated.length !== coverImages.length) {
        throw new Error('Failed to update images array properly');
      }

      dispatch(setCoverImages(updated));
      setCroppingImage(null);

    } catch (error) {
      console.error('Error in handleCropSave:', error);
      toast.error(`Failed to save cropped image: ${error.message}`);
    }
  };

  const handleImageUpload = (files) => {
    const newImages = files.map((file) => ({
      id: uuidv4(),
      url: file.url,
      publicId: file.publicId,
      file: file.file?.data || file.file,
      filename: file.filename,
      originalUrl: file.url,
      cropData: null,
    }));

    const existingFilenames = new Set(coverImages.map((img) => img.filename));
    const filtered = newImages.filter(
      (img) =>
        !existingFilenames.has(img.filename) &&
        !deletedFilenames.includes(img.filename)
    );
    dispatch(setCoverImages([...coverImages, ...filtered]));
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileDetails();
        if (res?.status === 200) {
          setProfileDetails(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch profile details:', err);
      }
    };
    fetchProfile();
  }, [dispatch]);

  const handleSketchGeneration = async () => {
    try {
      if (!activeBookId) {
        setIsOpenBookSelectionModal(true);
        return;
      }

      // Prevent generation if already uploading
      if (uploadingCoverImage) {
        saveStep({
          userId: session?.user?._id,
          bookId: activeBookId,
          isCompleted: 'yes',
          stepCode: 'Upload_Images',
        });
        dispatch(
          markStepAsVisited({
            id: activeBookId,
            currentStepCode: 'Upload_Images',
          })
        );
        navigate(`/create?stepCode=${'Upload_Images'}`);
        return;
      }

      // Check if image already generated
      if (!!coverImages[0]?.AIinfo?.aws_upload?.s3_url || !!coverImages[0]?.AIUrl) {
        setNextDisabled(false);
        saveStep({
          userId: session?.user?._id,
          bookId: activeBookId,
          isCompleted: 'yes',
          stepCode: 'Upload_Images',
        });
        dispatch(
          markStepAsVisited({
            id: activeBookId,
            currentStepCode: 'Upload_Images',
          })
        );
        navigate(`/create?stepCode=${'Upload_Images'}`);
        return;
      }

      // Validate we have images
      if (!coverImages || coverImages.length === 0) {
        toast.error('Please upload at least one image to continue.');
        return;
      }

      setNextDisabled(true);

      console.log('🚀 Starting cover generation process...');
      
      // 🆕 Process images: auto-crop non-cropped images, upload blob URLs to Cloudinary
      const processedImageUrls = await processAllImagesForRun(coverImages);

      if (!processedImageUrls || processedImageUrls.length === 0) {
        throw new Error('Failed to process images');
      }

      console.log('✅ All images processed and ready for /run:', processedImageUrls);

      // 🆕 Call backend API to start RunPod job with processed Cloudinary URLs
      const response = await run({
        imageUrls: processedImageUrls,
        title: 'Cover Image',
        bookId: activeBookId
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to start job');
      }

      console.log('✅ Job started:', response.data);

      // Save step and navigate
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: 'Upload_Images',
      });
      
      dispatch(
        markStepAsVisited({
          id: activeBookId,
          currentStepCode: 'Upload_Images',
        })
      );
      
      navigate(`/create?stepCode=${'Upload_Images'}`);

    } catch (err) {
      console.error('Error starting cover generation:', err);
      toast.error(
        err.message || 'Failed to start image generation. Please try again.'
      );
      setNextDisabled(false);
    }
  };

  const handleDeleteImage = (id) => {
    if (uploadingCoverImage) {
      toast.warning(
        "Cannot delete image while it's being processed. Please wait for generation to complete."
      );
      return;
    }

    const toDelete = coverImages.find((img) => img.id === id || img._id === id);
    if (toDelete) {
      setDeletedFilenames((prev) => [...prev, toDelete.filename]);
      dispatch(deleteCoverImage(id));
      removeBookImage(id);
    }
  };

  const renderLoadingOverlay = () => {
    if (!uploadingCoverImage) return null;

    return (
      <div className='absolute inset-0 bg-black/20 backdrop-blur-[1px] rounded-2xl z-20 flex items-center justify-center'>
        <div className='bg-white/90 rounded-full p-3 shadow-lg'>
          <div className='animate-spin rounded-full h-6 w-6 border-2 border-pink-500 border-t-transparent'></div>
        </div>
        <div className='absolute bottom-2 left-0 right-0 text-center'>
          <span className='text-xs font-medium text-white bg-black/50 px-2 py-1 rounded-full'>
            Generating...
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className='relative'>
        <h1 className='text-2xl md:text-3xl xl:text-4xl font-bold text-black mb-2 md:mb-4 xl:mb-6 text-center flex gap-5 items-center justify-center flex-col-reverse md:flex-row'>
          Upload Your Cover Photo{' '}
          <div className='h-6 md:h-9 flex items-center justify-center md:px-5 px-3 md:rounded-lg rounded-full border border-md-pink text-md-pink text-[10px] md:text-base'>
            Step {stepNumber} of 5
          </div>
        </h1>
        <StepProgressView number={stepNumber} />
        
        {!coverImages.length > 0 && (
          <>
            <div className='bg-transparent md:bg-white rounded-lg overflow-hidden py-3 px-2 md:px-6 flex items-center justify-between gap-2 lg:gap-4 mb-6 w-full'>
              <div className='flex-shrink-0'>
                <Button
                  buttonLabel='Back'
                  buttonClasses={'!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm'}
                  buttonLabelClasses={'text-sm !font-bold'}
                  buttonFunction={async () => {
                    saveStep({
                      userId: session?.user?._id,
                      bookId: activeBookId,
                      isCompleted: 'yes',
                      stepCode: 'Create_New_Book',
                    });
                    dispatch(
                      markStepAsVisited({
                        id: activeBookId,
                        currentStepCode: 'Create_New_Book',
                      })
                    );
                    navigate(`/create-book?stepCode=${'Create_New_Book'}`);
                  }}
                />
              </div>
              <div className='flex-grow flex-shrink max-w-md text-black font-bold text-sm lg:text-base text-center hidden md:block'>
                Upload your cover photo below.
              </div>
              <div className='flex-shrink-0'>
                <Button
                  buttonLabel={!!nextDisabled ? 'Generating...' : 'Next'}
                  buttonClasses={'!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm'}
                  buttonLabelClasses={'text-sm !font-bold'}
                  buttonFunction={handleSketchGeneration}
                  isDisable={coverImages.length === 0 || nextDisabled}
                />
              </div>
            </div>
            <div className='bg-white rounded-lg p-4 md:p-2.5'>
              <FileUploader onChange={handleImageUpload} single />
            </div>
          </>
        )}

        {coverImages.length > 0 && (
          <>
            <div className='bg-transparent md:bg-white rounded-lg overflow-hidden py-3 px-2 md:px-6 flex items-center justify-between gap-5 lg:gap-4 mb-6 w-full flex-wrap'>
              <div className='flex-shrink md:flex-shrink-0'>
                <Button
                  buttonLabel='Back'
                  buttonClasses={'!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm'}
                  buttonLabelClasses={'text-sm !font-bold'}
                  buttonFunction={async () => {
                    saveStep({
                      userId: session?.user?._id,
                      bookId: activeBookId,
                      isCompleted: 'yes',
                      stepCode: 'Create_New_Book',
                    });
                    dispatch(
                      markStepAsVisited({
                        id: activeBookId,
                        currentStepCode: 'Create_New_Book',
                      })
                    );
                    navigate(`/create-book?stepCode=${'Create_New_Book'}`);
                  }}
                />
              </div>
              <div className='flex-grow flex-shrink-0 md:flex-shrink max-w-md order-last md:order-none'>
                <div className='flex items-center justify-center gap-2 py-2 md:py-3 px-2 md:px-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-600 text-black font-bold'>
                  <i className='fa-solid fa-crop-simple text-base md:text-lg'></i>
                  <span className='text-sm lg:text-base'>
                    {uploadingCoverImage
                      ? 'Generating your cover...'
                      : 'Click to crop.'}
                  </span>
                </div>
              </div>
              <div className='flex-shrink md:flex-shrink-0'>
                <Button
                  buttonLabel={'Next'}
                  buttonClasses={'!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm'}
                  buttonLabelClasses={'text-sm !font-bold'}
                  buttonFunction={handleSketchGeneration}
                  isDisable={coverImages.length === 0 || nextDisabled}
                />
              </div>
            </div>

            <div className='bg-white rounded-lg overflow-hidden py-5 px-10'>
              <SortableImageGrid
                images={coverImages}
                setImages={(updated) => dispatch(setCoverImages(updated))}
                onEdit={(image) => {
                  if (uploadingCoverImage) {
                    toast.warning(
                      "Cannot edit image while it's being processed. Please wait for generation to complete."
                    );
                    return;
                  }
                  setCroppingImage(image);
                }}
                deletedFilenames={deletedFilenames}
                setDeletedFilenames={setDeletedFilenames}
                handleImageUpload={handleImageUpload}
                onDeleteImage={handleDeleteImage}
                isMulti={false}
                isSingleCover
                uploadingNewImages={!uploadingCoverImage || !coverImages[0]?.id ? [] : [coverImages[0].id]}
                isDisabled={uploadingCoverImage}
              />
              {/* {renderLoadingOverlay()} */}
            </div>
          </>
        )}
      </div>
      
      {croppingImage && (
        <CropperModal
          isOpen={!!croppingImage}
          setIsOpen={() => setCroppingImage(null)}
          imageUrl={croppingImage.url}
          originalUrl={croppingImage.originalUrl}
          cropData={croppingImage.cropData}
          onCrop={handleCropSave}
          aspectRatio={8 / 11}
        />
      )}
    </>
  );
}

export default UploadCoverPhoto;